import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { Auth } from './auth/auth';

export interface SiteStatus {
  maintenance_mode: boolean;
  maintenance_message: string;
  banners: { id: number; message: string; style: string }[];
  sidebar_config: Record<string, unknown>;
  document_watermarks_enabled?: boolean;
  email_enabled?: boolean;
  /** Initial-release feature flags: default OFF, toggled in Support > System. */
  feature_messages_enabled?: boolean;
  feature_support_enabled?: boolean;
  feature_bug_reports_enabled?: boolean;
  /** True when request was authenticated and user has Django is_staff. Enables bypass of maintenance overlay. */
  user_is_staff?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SiteStatusService {
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private platformId = inject(PLATFORM_ID);

  private readonly url = `${environment.apiBaseUrl}/support/site-status/`;

  private _status = signal<SiteStatus | null>(null);
  private _loaded = signal(false);

  status = this._status.asReadonly();
  loaded = this._loaded.asReadonly();

  maintenanceMode = computed(() => this._status()?.maintenance_mode ?? false);
  maintenanceMessage = computed(() => this._status()?.maintenance_message ?? '');
  banners = computed(() => this._status()?.banners ?? []);
  /** When true, PDFs show development watermark. Disabled via Support > System > Document watermarks. */
  documentWatermarksEnabled = computed(() => this._status()?.document_watermarks_enabled !== false);

  /** When false, emails are disabled (invites, etc.). Copy invite link shown in admin. */
  emailEnabled = computed(() => this._status()?.email_enabled !== false);

  /** Initial-release feature flags: default OFF (missing/false), toggled in Support > System. */
  messagesEnabled = computed(() => this._status()?.feature_messages_enabled === true);
  supportEnabled = computed(() => this._status()?.feature_support_enabled === true);
  bugReportsEnabled = computed(() => this._status()?.feature_bug_reports_enabled === true);

  /** Sidebar config: { default: { projects: true, orders: true, ... } } or flat { projects: true, ... } */
  sidebarConfig = computed(() => {
    const cfg = this._status()?.sidebar_config;
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return {};
    const def = (cfg as Record<string, unknown>)['default'];
    if (def && typeof def === 'object' && !Array.isArray(def)) {
      return def as Record<string, boolean>;
    }
    return cfg as Record<string, boolean>;
  });

  /** True if user can bypass maintenance (Django staff or platform admin) */
  canBypassMaintenance = computed(() => {
    const profiles = this.auth.getProfiles();
    const platformAdmin = profiles.some((p) =>
      p.support_role === 'platform_manager' ||
      p.support_role === 'platform_owner'
    );
    const userIsStaff = this._status()?.user_is_staff === true;
    return platformAdmin || userIsStaff;
  });

  /** True when maintenance is on and user should see the overlay (cannot bypass). Never shown on auth routes. */
  showMaintenanceOverlay = computed(() => {
    if (!this.maintenanceMode()) return false;
    return !this.canBypassMaintenance();
  });

  /** True when maintenance is on and user is staff - show banner instead of overlay */
  showMaintenanceBanner = computed(() => {
    return this.maintenanceMode() && this.canBypassMaintenance();
  });

  /** Check if a sidebar item is enabled by config. Default true if not specified. */
  isSidebarItemEnabled(key: string): boolean {
    const cfg = this.sidebarConfig();
    return cfg[key] !== false;
  }

  private static readonly DEFAULTS: SiteStatus = {
    maintenance_mode: false,
    maintenance_message: '',
    banners: [],
    sidebar_config: {},
    document_watermarks_enabled: true,
    email_enabled: true,
    feature_messages_enabled: false,
    feature_support_enabled: false,
    feature_bug_reports_enabled: false,
    user_is_staff: false,
  };

  load(): Observable<SiteStatus> {
    if (!isPlatformBrowser(this.platformId)) {
      this._status.set(SiteStatusService.DEFAULTS);
      this._loaded.set(true);
      return of(SiteStatusService.DEFAULTS);
    }
    return this.http.get<SiteStatus>(this.url).pipe(
      tap((data) => {
        this._status.set(data);
        this._loaded.set(true);
      }),
      catchError(() => {
        this._status.set(SiteStatusService.DEFAULTS);
        this._loaded.set(true);
        return of(SiteStatusService.DEFAULTS);
      })
    );
  }

  refresh(): void {
    this.load().subscribe();
  }
}
